import { AnimatePresence, motion } from 'framer-motion';
import {
  LuBookOpen,
  LuChartLine,
  LuChevronDown,
  LuCrown,
  LuGraduationCap,
  LuRoute,
  LuSparkles,
} from 'react-icons/lu';
import { Pill, ProgressBar } from '../../design-system/components';
import { countOf } from '../../utils/plural';
import { formatPercent } from '../../utils/percent';
import { PathAccordion } from './PathAccordion';
import type { DomainWithPaths, ExtendedLesson } from './types';

interface DomainAccordionProps {
  domain: DomainWithPaths;
  isExpanded: boolean;
  onToggle: () => void;
  expandedPaths: Record<number, boolean>;
  onTogglePath: (pathId: number) => void;
  pathLessons: Record<number, ExtendedLesson[]>;
  loadingPaths: Record<number, boolean>;
  errorPaths?: Record<number, boolean>;
  onRetryPath?: (pathId: number) => void;
  forcePathsExpanded?: boolean;
}

/**
 * A unit (domain) card on the Course page (redesign PR4, course.css): icon
 * square, title and counts, percent complete, the round toggle and the
 * progress bar. From 768 the header is the card and its chapters sit under
 * it, indented; on a phone the whole unit is one card. Open units carry the
 * orange edge, mastered ones gold. Same toggle, counts and states as before.
 */
export function DomainAccordion({
  domain,
  isExpanded,
  onToggle,
  expandedPaths,
  onTogglePath,
  pathLessons,
  loadingPaths,
  errorPaths = {},
  onRetryPath,
  forcePathsExpanded = false,
}: DomainAccordionProps) {
  const state = isExpanded ? 'expanded' : domain.mastered ? 'mastered' : 'collapsed';
  return (
    <motion.div
      data-domain-id={domain.id}
      className={`ee-course-unit ee-course-unit--${state}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Domain Header - Clickable */}
      <button type="button" onClick={onToggle} className="ee-course-unit__head">
        <span className="ee-course-unit__row">
          {/* Domain Icon */}
          <span
            className={`ee-course-tile ee-course-unit__icon ${domain.mastered ? 'ee-course-tile--gold' : ''}`}
          >
            {domain.mastered ? <LuCrown aria-hidden="true" /> : <LuBookOpen aria-hidden="true" />}
          </span>

          {/* Domain Info */}
          <span className="ee-course-unit__info">
            <span className="ee-course-unit__titles">
              <h2 className="ee-course-unit__title">{domain.description}</h2>
              {domain.mastered ? (
                <Pill variant="mastered">
                  <LuSparkles aria-hidden="true" />
                  MASTERED
                </Pill>
              ) : null}
            </span>
            <span className="ee-course-unit__meta">
              <span>
                <LuGraduationCap aria-hidden="true" />
                {domain.completed_lessons || 0}/{domain.total_lessons || 0} lessons
              </span>
              <span>
                <LuRoute aria-hidden="true" />
                {countOf(domain.paths.length, 'unit')}
              </span>
              {(domain.average_score ?? 0) > 0 && (
                <span>
                  <LuChartLine aria-hidden="true" />
                  {formatPercent(domain.average_score)} avg
                </span>
              )}
            </span>
          </span>

          {/* Progress & Expand */}
          <span className="ee-course-unit__end">
            <span className="ee-course-unit__pct">
              <span className="ee-course-unit__pct-value">{formatPercent(domain.progress)}</span>
              <span className="ee-course-unit__pct-label">complete</span>
            </span>
            <motion.span
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="ee-course-toggle"
            >
              <LuChevronDown aria-hidden="true" />
            </motion.span>
          </span>
        </span>

        {/* Progress Bar */}
        <ProgressBar
          value={domain.progress || 0}
          tone={domain.mastered ? 'gold' : 'orange'}
          animate
          className="ee-course-unit__bar"
        />
      </button>

      {/* Paths List - Expandable */}
      <AnimatePresence>
        {isExpanded ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="ee-course-unit__paths"
          >
            {domain.paths.length > 0 ? (
              domain.paths.map((path) => (
                <PathAccordion
                  key={path.id}
                  path={path}
                  isExpanded={expandedPaths[path.id] || forcePathsExpanded}
                  onToggle={() => onTogglePath(path.id)}
                  lessons={pathLessons[path.id] || []}
                  isLoadingLessons={loadingPaths[path.id] || false}
                  hasError={errorPaths[path.id] || false}
                  onRetry={onRetryPath ? () => onRetryPath(path.id) : undefined}
                />
              ))
            ) : (
              <div className="ee-chapter__note">
                <LuRoute className="ee-chapter__note-icon" aria-hidden="true" />
                No units available in this module
              </div>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}
